import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import categoryService from '../../services/categoryService';
import bonusQuestionService from '../../services/bonusQuestionService';
import ballotService from '../../services/ballotService';
import userService from '../../services/userService';
import { getCategoryWinnerIds } from '../../utils/categoryWinners';
import { useBeta } from '../../contexts/BetaContext';
import { useSettings } from '../../contexts/SettingsContext';
import { hasLeaderboardAccess } from '../../utils/leaderboardAccess';
import { LEADERBOARD_ACCESS_CODE } from '../../config/constants';
import './SecretBallotAdminPage.css';

const isMemoriamQuestion = (questionText = '') => (
  questionText.toLowerCase().includes('memoriam')
);

const SecretBallotAdminPage = () => {
  const navigate = useNavigate();
  const { activePartyId } = useBeta();
  const { settings } = useSettings();

  const [categories, setCategories] = useState([]);
  const [bonusQuestions, setBonusQuestions] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState('');

  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [loadingUser, setLoadingUser] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [existingBallot, setExistingBallot] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [bonusResponses, setBonusResponses] = useState({});

  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const hasAccess = hasLeaderboardAccess(LEADERBOARD_ACCESS_CODE);
  const guestFullName = `${guestFirstName} ${guestLastName}`.trim();

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      return orderA - orderB;
    });
  }, [categories]);

  const initPredictions = (cats) => {
    const next = {};
    cats.forEach((cat) => {
      next[cat.id] = '';
    });
    return next;
  };

  const initBonusResponses = (questions) => {
    const next = {};
    questions.forEach((question) => {
      next[question.id] = '';
    });
    return next;
  };

  useEffect(() => {
    if (!hasAccess) {
      navigate('/', { replace: true });
    }
  }, [hasAccess, navigate]);

  useEffect(() => {
    if (!activePartyId) {
      setLoadingMeta(false);
      return;
    }

    const fetchMeta = async () => {
      setLoadingMeta(true);
      setMetaError('');
      try {
        const [rawCategories, rawBonusQuestions] = await Promise.all([
          categoryService.fetchCategories(activePartyId),
          bonusQuestionService.fetchBonusQuestions(activePartyId),
        ]);

        const mappedCategories = rawCategories.map((category) => {
          const winnerIds = getCategoryWinnerIds(category);
          return {
            id: category.categoryId || category.id,
            name: category.name,
            points: category.points,
            order: category.order,
            nominees: (category.nominees || []).map((nominee) => ({
              id: nominee.nomineeId || nominee.id,
              name: nominee.nomineeText || nominee.name,
              code: nominee.nomineeCode || nominee.code,
              isWinner: winnerIds.has(nominee.nomineeId || nominee.id),
            })),
          };
        });

        const mappedBonusQuestions = rawBonusQuestions.map((question) => ({
          id: question.bonusQuestionId || question.id,
          question: question.question || '',
        }));

        setCategories(mappedCategories);
        setBonusQuestions(mappedBonusQuestions);
        setPredictions(initPredictions(mappedCategories));
        setBonusResponses(initBonusResponses(mappedBonusQuestions));
      } catch (error) {
        setMetaError(error.message || 'Failed to load ballot metadata.');
      } finally {
        setLoadingMeta(false);
      }
    };

    fetchMeta();
  }, [activePartyId]);

  const loadBallotForUser = async (userId) => {
    const ballot = await ballotService.fetchBallotById(activePartyId, userId);
    setExistingBallot(ballot);

    const nextPredictions = initPredictions(categories);
    if (ballot?.userCategoriesSelections) {
      ballot.userCategoriesSelections.forEach((selection) => {
        if (nextPredictions[selection.categoryId] !== undefined) {
          nextPredictions[selection.categoryId] = selection.selection?.nomineeId || '';
        }
      });
    }
    setPredictions(nextPredictions);

    const nextBonus = initBonusResponses(bonusQuestions);
    if (ballot?.userBonusQuestionsResponses) {
      ballot.userBonusQuestionsResponses.forEach((response) => {
        if (nextBonus[response.bonusQuestionId] !== undefined) {
          nextBonus[response.bonusQuestionId] = response.response || '';
        }
      });
    }
    setBonusResponses(nextBonus);
  };

  const handleLoadExistingByEmail = async () => {
    if (!guestEmail.trim()) {
      setErrorMessage('Email is required to load an existing ballot.');
      return;
    }

    setLoadingUser(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const user = await userService.getUserByEmail(guestEmail);
      if (!user) {
        setErrorMessage('No user found for this email.');
        return;
      }

      setSelectedUser(user);
      if (user?.name) {
        const nameParts = user.name.trim().split(/\s+/);
        setGuestFirstName(nameParts[0] || '');
        setGuestLastName(nameParts.slice(1).join(' ') || '');
      }
      await loadBallotForUser(user.id);
      setStatusMessage('Loaded existing user. Edit and save below.');
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load existing user.');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleCreateOrLoadGuest = async () => {
    if (!guestFirstName.trim() || !guestLastName.trim() || !guestEmail.trim()) {
      setErrorMessage('Guest first name, last name, and email are required.');
      return;
    }

    setLoadingUser(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const result = await userService.findOrCreateUser(guestEmail, guestFullName);
      const user = await userService.getUserById(result.userId);
      const resolvedUser = user || {
        id: result.userId,
        name: guestFullName,
        email: guestEmail.trim().toLowerCase(),
        displayName: guestFullName,
      };

      setSelectedUser(resolvedUser);
      await loadBallotForUser(result.userId);

      setStatusMessage(result.isNew
        ? 'Created new guest user. Fill out ballot and save.'
        : 'Loaded existing guest user. Edit and save below.');
    } catch (error) {
      setErrorMessage(error.message || 'Failed to create/load guest.');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSaveBallot = async () => {
    if (!selectedUser?.id) {
      setErrorMessage('Load or create a guest user first.');
      return;
    }

    setSaving(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const userCategoriesSelections = Object.entries(predictions)
        .filter(([_, nomineeId]) => nomineeId)
        .map(([categoryId, nomineeId]) => {
          const category = categories.find((cat) => cat.id === categoryId);
          const nominee = category?.nominees?.find((nom) => nom.id === nomineeId);
          return {
            categoryId,
            selection: {
              nomineeId,
              nomineeCode: nominee?.code || '',
              nomineeText: nominee?.name || '',
            },
          };
        });

      const userBonusQuestionsResponses = Object.entries(bonusResponses)
        .reduce((acc, [bonusQuestionId, response]) => {
          const question = bonusQuestions.find((item) => item.id === bonusQuestionId);
          const trimmedResponse = (response || '').trim();

          if (trimmedResponse) {
            acc.push({
              bonusQuestionId,
              response: trimmedResponse,
            });
            return acc;
          }

          if (isMemoriamQuestion(question?.question || '')) {
            acc.push({
              bonusQuestionId,
              response: '0',
            });
          }

          return acc;
        }, []);

      const allAnswered = sortedCategories.length > 0
        && sortedCategories.every((category) => Boolean(predictions[category.id]));

      const ballotData = {
        userCategoriesSelections,
        userBonusQuestionsResponses,
        isDraft: !allAnswered,
      };

      if (existingBallot?.id) {
        await ballotService.updateBallot(activePartyId, existingBallot.id, ballotData);
        setStatusMessage('Ballot updated successfully.');
      } else {
        await ballotService.createBallot(activePartyId, {
          oscarsPartyId: activePartyId,
          userId: selectedUser.id,
          userCategoriesSelections,
          userBonusQuestionsResponses,
          isDraft: !allAnswered,
        });
        setStatusMessage('New ballot created successfully.');
      }

      await loadBallotForUser(selectedUser.id);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save ballot.');
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="secret-admin-page">
      <div className="secret-admin-page__container">
        <header className="secret-admin-page__header">
          <h1>Secret Ballot Admin</h1>
          <p>
            Party: <strong>{settings?.partyName || 'MR Oscar Party'}</strong>
          </p>
          <p className="secret-admin-page__note">
            This page bypasses normal ballot submission deadline rules.
          </p>
        </header>

        <section className="secret-admin-card">
          <h2>Load or Create Guest</h2>
          <div className="secret-admin-grid">
            <label>
              First Name
              <input
                type="text"
                value={guestFirstName}
                onChange={(event) => setGuestFirstName(event.target.value)}
                placeholder="First"
              />
            </label>
            <label>
              Last Name
              <input
                type="text"
                value={guestLastName}
                onChange={(event) => setGuestLastName(event.target.value)}
                placeholder="Last"
              />
            </label>
            <label>
              Guest Email
              <input
                type="email"
                value={guestEmail}
                onChange={(event) => setGuestEmail(event.target.value)}
                placeholder="guest@example.com"
              />
            </label>
          </div>
          <div className="secret-admin-actions">
            <button type="button" onClick={handleLoadExistingByEmail} disabled={loadingMeta || loadingUser}>
              Load Existing by Email
            </button>
            <button type="button" onClick={handleCreateOrLoadGuest} disabled={loadingMeta || loadingUser}>
              Create/Load Guest
            </button>
          </div>
          {selectedUser && (
            <p className="secret-admin-selected">
              Editing user: <strong>{selectedUser.displayName || selectedUser.name || selectedUser.email}</strong>
              {' '}({selectedUser.email || guestEmail})
              {' '}| Ballot: <strong>{existingBallot ? 'Existing' : 'New'}</strong>
            </p>
          )}
        </section>

        {metaError && <p className="secret-admin-error">{metaError}</p>}
        {errorMessage && <p className="secret-admin-error">{errorMessage}</p>}
        {statusMessage && <p className="secret-admin-status">{statusMessage}</p>}

        <section className="secret-admin-card">
          <h2>Ballot Editor</h2>
          {loadingMeta ? (
            <p>Loading categories and bonus questions...</p>
          ) : (
            <>
              <div className="secret-admin-ballot">
                {sortedCategories.map((category) => (
                  <div key={category.id} className="secret-admin-category">
                    <h3>{category.order}. {category.name}{category.points ? ` (${category.points} pts)` : ''}</h3>
                    <div className="secret-admin-nominees">
                      {(category.nominees || []).map((nominee) => (
                        <label key={nominee.id}>
                          <input
                            type="radio"
                            name={`secret-category-${category.id}`}
                            value={nominee.id}
                            checked={predictions[category.id] === nominee.id}
                            onChange={() => {
                              setPredictions((prev) => ({
                                ...prev,
                                [category.id]: nominee.id,
                              }));
                            }}
                          />
                          <span>{nominee.code}. {nominee.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                {bonusQuestions.length > 0 && (
                  <div className="secret-admin-bonus">
                    <h3>Bonus Questions</h3>
                    {bonusQuestions.map((question) => (
                      <label key={question.id}>
                        <span>{question.question}</span>
                        <input
                          type="text"
                          value={bonusResponses[question.id] || ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            setBonusResponses((prev) => ({
                              ...prev,
                              [question.id]: value,
                            }));
                          }}
                          placeholder="Enter response"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="secret-admin-actions">
                <button
                  type="button"
                  onClick={handleSaveBallot}
                  disabled={!selectedUser || saving}
                >
                  {saving ? 'Saving...' : 'Save Ballot'}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => navigate('/leaderboard')}
                >
                  Back to Leaderboard
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default SecretBallotAdminPage;
